import type { NetworkType } from './networkType';
import type {
  EarthingCompliance,
  EarthingLimitCalculationStatus,
  EarthingMeasurement,
} from '../modules/inspectors/services/controlTaskService';
import { validateRcdDecimal } from '../modules/inspectors/utils/rcdMeasurements';

// Integer ratios preserve Java BigDecimal comparisons and HALF_UP rounding exactly.
type Decimal = { numerator: bigint; denominator: bigint };
const decimal = (value: unknown): Decimal | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const text = String(value).trim();
  if (!/^(?:\d+\.?\d*|\.\d+)$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return { numerator: BigInt((whole || '0') + fraction), denominator: BigInt('1' + '0'.repeat(fraction.length)) };
};
const integer = (value: number): Decimal => ({ numerator: BigInt(value), denominator: BigInt(1) });
const compare = (a: Decimal, b: Decimal) => {
  const diff = a.numerator * b.denominator - b.numerator * a.denominator;
  return diff > BigInt(0) ? 1 : diff < BigInt(0) ? -1 : 0;
};
const multiply = (value: Decimal, multiplier: number): Decimal => ({
  numerator: value.numerator * BigInt(multiplier), denominator: value.denominator,
});
const divideToTwoDecimals = (numerator: Decimal, denominator: Decimal): string | null => {
  if (denominator.numerator <= BigInt(0)) return null;
  const scaledNumerator = numerator.numerator * denominator.denominator * BigInt(100);
  const scaledDenominator = numerator.denominator * denominator.numerator;
  const cents = (scaledNumerator * BigInt(2) + scaledDenominator) / (scaledDenominator * BigInt(2));
  return `${cents / BigInt(100)}.${String(cents % BigInt(100)).padStart(2, '0')}`;
};
const positive = (value: Decimal | null): value is Decimal => value !== null && value.numerator > BigInt(0);
const equalsInteger = (value: Decimal | null, expected: number) =>
  value !== null && compare(value, integer(expected)) === 0;
const curveFactors = new Map<string, number>([['B', 5], ['NH3', 5], ['C', 10], ['D', 15], ['TMŞ', 10]]);
const missingEnvironmentGroups = new Set(['MAIN_PANEL', 'SUB_PANEL', 'SOCKET']);
const noteFiveNetworkTypes = new Set<NetworkType>(['TT', 'TN-S', 'TN-CS']);

export type EarthingLimitCalculation = {
  limitValueZsRsOhm: string | null;
  status: EarthingLimitCalculationStatus;
  tripCurrentIaA: Decimal | null;
  protectiveDeviceLimitOhm: string | null;
  rcdLimitOhm: string;
  toroidLimitOhm: string;
  deviceSuitable: boolean | null;
  tripTestPassed: boolean | null;
  tripCurrentSuitable: boolean | null;
  tripTimeSuitable: boolean | null;
};

const calculateProtectiveLimit = (
  networkType: NetworkType | undefined,
  ia: Decimal | null,
  row: EarthingMeasurement,
): string | null => {
  if (row.measurementPointGroup === 'BODY' || row.measurementPointGroup === 'FIRE_PANEL') return '1.00';
  if (!networkType || !positive(ia)) return null;
  if (networkType !== 'TT') return divideToTwoDecimals(integer(230), ia);
  if (!row.measurementEnvironmentCondition) return null;
  return divideToTwoDecimals(integer(row.measurementEnvironmentCondition === 'DRY' ? 50 : 25), ia);
};

const calculateRcdLimit = (
  networkType: NetworkType | undefined,
  row: EarthingMeasurement,
  rcdRating: Decimal | null,
): string => {
  if (networkType === 'TN' || networkType === 'TN-C') return '0.00';
  if (!positive(rcdRating) || !row.measurementEnvironmentCondition) return '0.00';
  const contactVoltage = row.measurementEnvironmentCondition === 'DRY' ? 50 : 25;
  return divideToTwoDecimals(integer(contactVoltage * 1000), rcdRating) ?? '0.00';
};

const calculateToroidLimit = (
  networkType: NetworkType | undefined,
  toroidRating: Decimal | null,
): string => {
  if (networkType === 'TN' || networkType === 'TN-C' || !positive(toroidRating)) return '0.00';
  return divideToTwoDecimals(integer(50), toroidRating) ?? '0.00';
};

const controlsApply = (row: EarthingMeasurement, rcdRating: Decimal | null, toroidRating: Decimal | null) =>
  !!row.measurementPointGroup && row.measurementPointGroup !== 'BODY'
  && (rcdRating !== null || toroidRating !== null);

const permittedDevice = (row: EarthingMeasurement, milliampValue: Decimal | null) => {
  if (row.measurementPointGroup === 'MAIN_PANEL') {
    return equalsInteger(milliampValue, 30) || equalsInteger(milliampValue, 300);
  }
  if (row.measurementPointGroup === 'SUB_PANEL' || row.measurementPointGroup === 'SOCKET') {
    return equalsInteger(milliampValue, 30);
  }
  return false;
};

const maxCandidate = (...values: Array<string | null>): string | null => values.reduce<string | null>(
  (maximum, value) => {
    if (value === null) return maximum;
    if (maximum === null) return value;
    const parsedValue = decimal(value);
    const parsedMaximum = decimal(maximum);
    return parsedValue && parsedMaximum && compare(parsedValue, parsedMaximum) > 0 ? value : maximum;
  }, null,
);

const isBlankValue = (value: unknown) => value === undefined || value === null || String(value).trim() === '';

const matchesNoteFive = (networkType: NetworkType | undefined, row: EarthingMeasurement) => {
  const ratedCurrent = decimal(row.ratedCurrentA);
  const tripCurrent = decimal(row.rcdTripCurrentMa);
  const missingOrHighTripCurrent = row.rcdTripCurrentDidNotTrip !== true
    && (isBlankValue(row.rcdTripCurrentMa)
      || (tripCurrent !== null && compare(tripCurrent, integer(30)) > 0));
  return !!networkType && noteFiveNetworkTypes.has(networkType)
    && row.measurementPointGroup === 'SOCKET'
    && ratedCurrent !== null && compare(ratedCurrent, integer(32)) <= 0
    && missingOrHighTripCurrent;
};

export const calculateEarthingLimit = (
  networkType: NetworkType | undefined,
  row: EarthingMeasurement,
): EarthingLimitCalculation => {
  const ratedCurrent = decimal(row.ratedCurrentA);
  const factor = curveFactors.get(row.tripCurveTypeOrModel?.trim().toUpperCase() ?? '');
  const tripCurrentIaA = positive(ratedCurrent) && factor ? multiply(ratedCurrent, factor) : null;
  const rcdRating = decimal(row.rcdRatingMa);
  const toroidRating = decimal(row.toroidRatingA);
  const measuredTripCurrent = row.rcdTripCurrentDidNotTrip ? null : decimal(row.rcdTripCurrentMa);
  const measuredTripTime = row.rcdTripTimeDidNotTrip ? null : decimal(row.rcdTripTimeMs);
  const protectiveDeviceLimitOhm = calculateProtectiveLimit(networkType, tripCurrentIaA, row);
  const rcdLimitOhm = calculateRcdLimit(networkType, row, rcdRating);
  const toroidLimitOhm = calculateToroidLimit(networkType, toroidRating);
  const applyControls = controlsApply(row, rcdRating, toroidRating);
  const toroidMilliamp = toroidRating ? multiply(toroidRating, 1000) : null;
  const deviceSuitable = applyControls
    ? permittedDevice(row, rcdRating) || permittedDevice(row, toroidMilliamp) : null;
  const tripTestPassed = applyControls
    ? row.rcdTripCurrentDidNotTrip ? false : measuredTripCurrent !== null
    : null;
  const tripCurrentSuitable = applyControls
    ? measuredTripCurrent === null
      || !!(rcdRating && compare(measuredTripCurrent, rcdRating) <= 0)
      || !!(toroidMilliamp && compare(measuredTripCurrent, toroidMilliamp) <= 0)
    : null;
  const tripTimeSuitable = applyControls
    ? row.rcdTripTimeDidNotTrip ? false
      : measuredTripTime === null
      || !!(rcdRating && compare(measuredTripTime, integer(200)) <= 0)
      || !!(toroidRating && compare(measuredTripTime, integer(1000)) <= 0)
    : null;

  if (networkType === 'TT' && !row.measurementEnvironmentCondition
      && missingEnvironmentGroups.has(row.measurementPointGroup ?? '')) {
    return { limitValueZsRsOhm: null, status: 'MISSING_ENVIRONMENT', tripCurrentIaA,
      protectiveDeviceLimitOhm, rcdLimitOhm, toroidLimitOhm, deviceSuitable, tripTestPassed,
      tripCurrentSuitable, tripTimeSuitable };
  }
  if (row.measurementPointGroup === 'BODY' || row.measurementPointGroup === 'FIRE_PANEL') {
    return { limitValueZsRsOhm: '1.00', status: 'CALCULATED', tripCurrentIaA,
      protectiveDeviceLimitOhm, rcdLimitOhm, toroidLimitOhm, deviceSuitable, tripTestPassed,
      tripCurrentSuitable, tripTimeSuitable };
  }

  const controlFailed = [deviceSuitable, tripTestPassed, tripCurrentSuitable, tripTimeSuitable]
    .some(value => value === false);
  const hasUsableCandidate = protectiveDeviceLimitOhm !== null
    || compare(decimal(rcdLimitOhm)!, integer(0)) > 0
    || compare(decimal(toroidLimitOhm)!, integer(0)) > 0;
  const limitValueZsRsOhm = controlFailed ? protectiveDeviceLimitOhm
    : hasUsableCandidate ? maxCandidate(protectiveDeviceLimitOhm, rcdLimitOhm, toroidLimitOhm) : null;
  return { limitValueZsRsOhm,
    status: limitValueZsRsOhm === null ? 'INSUFFICIENT_DATA' : 'CALCULATED',
    tripCurrentIaA, protectiveDeviceLimitOhm, rcdLimitOhm, toroidLimitOhm,
    deviceSuitable, tripTestPassed, tripCurrentSuitable, tripTimeSuitable };
};

export const calculateEarthingLimitValue = (
  networkType: NetworkType | undefined,
  row: EarthingMeasurement,
): string | null => calculateEarthingLimit(networkType, row).limitValueZsRsOhm;

export const validateEarthingMeasuredValue = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  if (!text || text === '-') return undefined;
  if (Number(text) < 0) return 'Negatif değer girilemez.';
  if (!decimal(text)) return 'Sayı veya - giriniz.';
  return validateRcdDecimal(text);
};

export const calculateEarthingMeasurement = (
  networkType: NetworkType | undefined,
  row: EarthingMeasurement,
): EarthingMeasurement => {
  const limitCalculation = calculateEarthingLimit(networkType, row);
  const limitValueZsRsOhm = limitCalculation.limitValueZsRsOhm;
  let generatedCompliance: EarthingCompliance | null = null;
  if (row.measurementPointDescription?.trim()) {
    const measured = decimal(row.phaseEarthLoopImpedanceOhm);
    const limit = decimal(limitValueZsRsOhm);
    if (row.phaseEarthLoopImpedanceDash) {
      generatedCompliance = 'NOT_3';
    } else if (matchesNoteFive(networkType, row)) {
      generatedCompliance = 'NOT_5';
    } else if (measured && limit && compare(measured, limit) > 0) {
      generatedCompliance = 'NOT_2';
    } else {
      let reference: Decimal | null = null;
      const current = decimal(row.ratedCurrentA);
      const factor = curveFactors.get(row.tripCurveTypeOrModel?.trim().toUpperCase() ?? '');
      if (row.measurementPointGroup === 'BODY' || row.measurementPointGroup === 'FIRE_PANEL') {
        reference = integer(1);
      } else if (positive(current) && factor && networkType) {
        const voltage = networkType !== 'TT' ? 230
          : row.measurementEnvironmentCondition === 'DRY' ? 50
            : row.measurementEnvironmentCondition ? 25 : null;
        if (voltage !== null) reference = {
          numerator: BigInt(voltage) * current.denominator,
          denominator: current.numerator * BigInt(factor),
        };
      }
      generatedCompliance = measured && limit && reference && compare(measured, limit) < 0
        && compare(measured, reference) > 0 ? 'NOT_4' : 'NOT_1';
    }
  }
  const complianceOverridden = row.complianceOverridden === true;
  return { ...row, limitValueZsRsOhm,
    limitValueCalculationStatus: limitCalculation.status,
    generatedCompliance,
    compliance: complianceOverridden ? row.compliance ?? null : generatedCompliance,
    complianceOverridden };
};
